import {
  createParticles,
  updateParticle,
  drawBackgroundParticle,
  CursorPosition,
} from './particles';

export const createElementAnimation = (
  container: HTMLElement,
  subType: string,
  config: Record<string, unknown>
): (() => void) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  const rect = container.getBoundingClientRect();
  canvas.width = rect.width;
  canvas.height = rect.height;
  canvas.style.cssText = `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
  `;
  container.style.position = 'relative';
  container.style.zIndex = '1';
  canvas.style.zIndex = '-1';
  container.insertBefore(canvas, container.firstChild);

  // Pass element size for scaled particle count
  const particles = createParticles(canvas, config, subType, {
    width: rect.width,
    height: rect.height,
  });

  // Cursor tracking
  let cursorPos: CursorPosition | null = null;

  const handleMouseMove = (e: MouseEvent) => {
    const rect = container.getBoundingClientRect();
    cursorPos = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const handleMouseLeave = () => {
    cursorPos = null;
  };

  container.addEventListener('mousemove', handleMouseMove);
  container.addEventListener('mouseleave', handleMouseLeave);

  let animationId: number;

  const animate = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    for (const p of particles) {
      updateParticle(p, canvas, config, subType, cursorPos);
      drawBackgroundParticle(ctx, p, subType, config, canvas);
    }

    animationId = requestAnimationFrame(animate);
  };

  animate();

  return () => {
    cancelAnimationFrame(animationId);
    container.removeEventListener('mousemove', handleMouseMove);
    container.removeEventListener('mouseleave', handleMouseLeave);
    if (canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    }
  };
};

export const generateCategoryCSS = (category: string, config: Record<string, unknown>): string => {
  const bgColor = Array.isArray(config.color)
    ? config.color[0]
    : (config.color as string) || '#3498db';
  const opacity = (config.opacity as number) || 1;

  switch (category) {
    case 'backgrounds':
      return '';
    case 'buttons':
      return `
        position: relative;
        border: ${(config.borderWidth as number) || 2}px solid ${bgColor} !important;
        transition: all ${0.3 / ((config.speed as number) || 1)}s ease !important;
        box-shadow: 0 0 ${5 + ((config.intensity as number) || 0.5) * 15}px ${bgColor} !important;
      `;
    case 'text':
      return `
        background: linear-gradient(90deg, ${bgColor}, ${bgColor}cc, ${bgColor}) !important;
        background-size: 200% 100% !important;
        -webkit-background-clip: text !important;
        -webkit-text-fill-color: transparent !important;
        background-clip: text !important;
        animation: text-shine ${3 / ((config.speed as number) || 1)}s linear infinite !important;
      `;
    case 'loaders':
      return `
        width: ${(config.size as number) || 20}px !important;
        height: ${(config.size as number) || 20}px !important;
        border: ${Math.max(2, ((config.size as number) || 20) / 7)}px solid ${bgColor}22 !important;
        border-top-color: ${bgColor} !important;
        border-radius: 50% !important;
        animation: spin ${1 / ((config.speed as number) || 1)}s linear infinite !important;
      `;
    case 'cards':
      return `
        transition: transform 0.3s ease, box-shadow 0.3s ease !important;
        animation: card-float ${4 / ((config.speed as number) || 1)}s ease-in-out infinite !important;
      `;
    default:
      return '';
  }
};

export const injectAnimationKeyframes = (): void => {
  const styleId = 'animation-keyframes';
  if (document.getElementById(styleId)) return;

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes text-shine {
      0% { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
    @keyframes spin {
      to { transform: rotate(360deg); }
    }
    @keyframes card-float {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-10px); }
    }
    @keyframes btn-pulse {
      0%, 100% { box-shadow: 0 0 5px currentColor; }
      50% { box-shadow: 0 0 25px currentColor, 0 0 35px currentColor; }
    }
    @keyframes firefly-run {
      0% { --firefly-pos: 0%; }
      100% { --firefly-pos: 100%; }
    }
  `;
  document.head.appendChild(style);
};

// Generate minimal CSS for Firefly - just ensure overflow is visible
// Note: we don't change background/border to avoid breaking user's styling
export const generateFireflyCSS = (config: Record<string, unknown>): string => {
  injectAnimationKeyframes();

  return `
    overflow: visible !important;
  `;
};

// Apply Firefly animation to element - canvas UNDER the element
export const applyFireflyAnimation = (
  element: HTMLElement,
  config: Record<string, unknown>
): (() => void) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return () => {};

  const rect = element.getBoundingClientRect();
  const fireflyGlow = (config.fireflyGlow as number) || 15;
  const offset = Math.max(fireflyGlow, 8);
  canvas.width = rect.width + offset * 2;
  canvas.height = rect.height + offset * 2;

  // Use fixed positioning to position canvas relative to viewport
  // This avoids affecting the element's layout
  const elemRect = element.getBoundingClientRect();
  canvas.style.cssText = `
    position: fixed;
    top: ${elemRect.top - offset}px;
    left: ${elemRect.left - offset}px;
    width: ${elemRect.width + offset * 2}px;
    height: ${elemRect.height + offset * 2}px;
    pointer-events: none;
    z-index: -1;
  `;

  // Insert canvas to body to avoid affecting element's position
  document.body.appendChild(canvas);

  // Update canvas position on scroll/resize
  const updatePosition = () => {
    const rect = element.getBoundingClientRect();
    canvas.style.top = `${rect.top - offset}px`;
    canvas.style.left = `${rect.left - offset}px`;
  };

  window.addEventListener('scroll', updatePosition, { passive: true });
  window.addEventListener('resize', updatePosition, { passive: true });

  const borderWidth = (config.borderWidth as number) || 2;
  const borderRadius = (config.borderRadius as number) || 8;
  // Use already declared fireflyGlow and offset
  const btnWidth = rect.width - borderWidth * 2;
  const btnHeight = rect.height - borderWidth * 2;
  const btnX = borderWidth - offset;
  const btnY = borderWidth - offset;

  const fireflySize = (config.fireflySize as number) || 6;
  const fireflySpeedVal = (config.fireflySpeed as number) || 1;
  const fireflyColor = (config.fireflyColor as string) || '#00ffaa';
  const intensity = (config.intensity as number) || 0.5;

  let animationId: number;

  const animate = () => {
    const time = Date.now() * 0.001 * fireflySpeedVal;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const perimeter = (btnWidth + btnHeight) * 2;
    const position = (time * 50) % perimeter;

    let fireflyX = 0;
    let fireflyY = 0;

    if (position < btnWidth) {
      fireflyX = btnX + position;
      fireflyY = btnY;
    } else if (position < btnWidth + btnHeight) {
      fireflyX = btnX + btnWidth;
      fireflyY = btnY + (position - btnWidth);
    } else if (position < btnWidth * 2 + btnHeight) {
      fireflyX = btnX + btnWidth - (position - btnWidth - btnHeight);
      fireflyY = btnY + btnHeight;
    } else {
      fireflyX = btnX;
      fireflyY = btnY + btnHeight - (position - btnWidth * 2 - btnHeight);
    }

    const blinkAlpha = 0.5 + Math.sin(time * 10) * 0.5 * intensity + 0.5;

    // Single glow
    ctx.globalAlpha = blinkAlpha * 0.6;
    const gradient = ctx.createRadialGradient(
      fireflyX,
      fireflyY,
      0,
      fireflyX,
      fireflyY,
      fireflyGlow * 2
    );
    gradient.addColorStop(0, fireflyColor);
    gradient.addColorStop(0.3, fireflyColor + '80');
    gradient.addColorStop(1, 'transparent');
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(fireflyX, fireflyY, fireflyGlow * 2, 0, Math.PI * 2);
    ctx.fill();

    // Main dot
    ctx.globalAlpha = blinkAlpha;
    ctx.fillStyle = fireflyColor;
    ctx.beginPath();
    ctx.arc(fireflyX, fireflyY, fireflySize, 0, Math.PI * 2);
    ctx.fill();

    // Core
    ctx.globalAlpha = blinkAlpha * 0.9;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(fireflyX, fireflyY, fireflySize * 0.4, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1;
    animationId = requestAnimationFrame(animate);
  };

  animate();

  return () => {
    cancelAnimationFrame(animationId);
    window.removeEventListener('scroll', updatePosition);
    window.removeEventListener('resize', updatePosition);
    if (canvas.parentNode) {
      canvas.parentNode.removeChild(canvas);
    }
  };
};
